/**
 * Protocol Validation Schemas — Zod at trust boundaries ONLY.
 *
 * Apply validation at:
 *  1. QR/URL parse result → SharePayloadSchema
 *  2. Peer-supplied manifest → ManifestSchema
 *  3. Native bridge file list → FileMetadataSchema
 *
 * Do NOT run Zod on internal objects constructed by our own code.
 */

import { z } from 'zod';

// Share Protocol v1: BitTorrent v1 only = SHA-1 infohash (40 hex chars)
export const SharePayloadSchema = z.object({
  version: z.literal(1),
  shareId: z
    .string()
    .regex(/^[0-9a-f]{32}$/i, 'shareId must be 32 hex characters (16 bytes CSPRNG)'),
  infoHash: z
    .string()
    .regex(
      /^([0-9a-f]{40}|[0-9a-f]{64})$/i,
      'infoHash must be 40 hex chars (v1) or 64 hex chars (v2)'
    ),
  manifestHash: z
    .string()
    .regex(/^([0-9a-f]{40}|)$/, 'manifestHash must be 40 hex chars or empty'),
  flags: z.number().int().min(0).max(63),
  checksum: z.number().int(),
});

export type ValidatedSharePayload = z.infer<typeof SharePayloadSchema>;

export const FileMetadataSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(255),
  size: z.number().int().positive(),
  mimeType: z.string().min(1).max(127),
  // relativePath validated separately by sanitizeRelativePath()
  relativePath: z.string().max(4096).optional(),
});

export type ValidatedFileMetadata = z.infer<typeof FileMetadataSchema>;

export const ManifestSchema = z.object({
  version: z.literal(1),
  files: z.array(FileMetadataSchema).min(1).max(100_000),
  createdAt: z.number().int().positive(),
});

export type ValidatedManifest = z.infer<typeof ManifestSchema>;
